<template>
  <HeaderBar />

  <section class="carvia-container !max-w-full">
    <div class="mb-6">
      <Tabs v-model="activeQueue" :tabs="queueTabs" class="mt-6" query-param="queue" />
    </div>

    <div v-if="activeQueue" class="mt-8">
      <Tabs v-model="activeStatus" :tabs="statusTabs" class="mb-4" query-param="status" />

      <div class="rounded-lg bg-white p-4">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b">
                <th class="px-4 py-2 text-left">{{ t('id') }}</th>
                <th class="px-4 py-2 text-left">{{ t('name') }}</th>
                <th class="px-4 py-2 text-left">{{ t('progress') }}</th>
                <th class="px-4 py-2 text-left">{{ t('attempts') }}</th>
                <th class="px-4 py-2 text-left">{{ t('timestamp') }}</th>
                <th class="px-4 py-2 text-left">{{ t('data') }}</th>
                <th class="px-4 py-2 text-left">{{ t('actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="job in jobs" :key="job.id" class="border-b hover:bg-gray-50">
                <td class="px-4 py-2">{{ job.id }}</td>
                <td class="px-4 py-2">{{ job.name }}</td>
                <td class="px-4 py-2">
                  <div class="h-2.5 w-full rounded-full bg-gray-200">
                    <div
                      class="h-2.5 rounded-full bg-green-700"
                      :style="{ width: `${getJobProgressValue(job) * 100}%` }"
                    ></div>
                  </div>
                </td>
                <td class="px-4 py-2">{{ job.attemptsMade }}</td>
                <td class="px-4 py-2">{{ formatDate(job.timestamp) }}</td>
                <td class="px-4 py-2">
                  <div v-if="job.data" class="max-w-md">
                    <div v-if="!expandedJobs.has(job.id)">
                      <pre class="whitespace-pre-wrap font-mono text-xs">{{
                        truncateJson(job.data)
                      }}</pre>
                      <a
                        class="cursor-pointer text-xs hover:underline"
                        @click="toggleJobExpand(job.id)"
                      >
                        {{ t('showMore') }}
                      </a>
                    </div>
                    <div v-else>
                      <pre class="whitespace-pre-wrap font-mono text-xs">{{
                        JSON.stringify(job.data, null, 2)
                      }}</pre>
                      <a
                        class="cursor-pointer text-xs hover:underline"
                        @click="toggleJobExpand(job.id)"
                      >
                        {{ t('showLess') }}
                      </a>
                    </div>
                  </div>
                </td>
                <td class="px-4 py-2">
                  <div class="flex gap-2">
                    <CVButton variant="ghost" size="sm" @click="viewJobLogs(job.id)">
                      {{ t('viewLogs') }}
                    </CVButton>
                    <CVButton
                      v-if="
                        job.status === QueueJobStatus.FAILED ||
                        job.status === QueueJobStatus.COMPLETED
                      "
                      variant="ghost"
                      size="sm"
                      :is-loading="isRetrying"
                      @click="handleRetry(job.id)"
                    >
                      {{ t('retry') }}
                    </CVButton>
                    <CVButton
                      variant="ghost"
                      size="sm"
                      :is-loading="isDeleting"
                      @click="handleDelete(job.id)"
                    >
                      <AlertCircleIcon
                        v-if="job.status === QueueJobStatus.FAILED"
                        class="h-4 w-4 text-bittersweet-600"
                      />
                      {{ t('delete') }}
                    </CVButton>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="hasNextPage" class="mt-4 flex justify-center">
          <CVButton outline :is-loading="isFetchingNextPage" @click="fetchNextPage">
            {{ t('loadMore') }}
          </CVButton>
        </div>
      </div>
    </div>

    <Modal v-model="showLogsModal">
      <div v-if="selectedJobLogs" class="max-h-[60vh] overflow-y-auto">
        <div
          v-for="(log, index) in selectedJobLogs"
          :key="index"
          class="whitespace-pre-wrap font-mono text-xs"
        >
          {{ log }}
        </div>
      </div>
    </Modal>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import Tabs, { type TabItem } from '@/components/Tabs.vue';
import { useUiStore } from '@/stores/ui.store';
import {
  useAvailableQueues,
  useDeleteJob,
  useJobLogs,
  useQueueJobs,
  useQueueStats,
  useRetryJob,
} from '@/queries/use-queue-inspector';
import { type JobDto, QueueJobStatus } from '@carvia/ros-client-types';
import Modal from '@/components/Modal.vue';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { Alert } from '@/services/alert.service';
import { AlertCircleIcon } from 'lucide-vue-next';

const { t } = useI18n();
const uiStore = useUiStore();

watchEffect(() => {
  uiStore.setHeaderTitle(t('queueInspector'));
});

const activeQueue = ref<string>('');
const queueTabs = computed<TabItem[]>(
  () =>
    availableQueues.value?.map((queue) => ({
      title: queue,
      id: queue,
    })) || [],
);

const activeStatus = ref<QueueJobStatus>(QueueJobStatus.ACTIVE);
const { data: stats } = useQueueStats(activeQueue);
const statusTabs = computed<TabItem[]>(() => [
  {
    title: computed(() => `${t('active')} ${stats.value?.active ? `(${stats.value.active})` : ''}`),
    id: QueueJobStatus.ACTIVE,
  },
  {
    title: computed(
      () => `${t('waiting')} ${stats.value?.waiting ? `(${stats.value.waiting})` : ''}`,
    ),
    id: QueueJobStatus.WAITING,
  },
  {
    title: computed(
      () => `${t('delayed')} ${stats.value?.delayed ? `(${stats.value.delayed})` : ''}`,
    ),
    id: QueueJobStatus.DELAYED,
  },
  {
    title: computed(() => `${t('failed')} ${stats.value?.failed ? `(${stats.value.failed})` : ''}`),
    id: QueueJobStatus.FAILED,
  },
  {
    title: computed(
      () => `${t('completed')} ${stats.value?.completed ? `(${stats.value.completed})` : ''}`,
    ),
    id: QueueJobStatus.COMPLETED,
  },
  {
    title: computed(
      () =>
        `${t('waitingChildren')} ${stats.value?.waitingChildren ? `(${stats.value.waitingChildren})` : ''}`,
    ),
    id: QueueJobStatus.WAITING_CHILDREN,
  },
]);

const {
  data: jobsData,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
} = useQueueJobs(
  computed(() => activeQueue.value),
  computed(() => [activeStatus.value]),
);

const jobs = useFlattenPaginatedData(jobsData);

const showLogsModal = ref<boolean>(false);
const selectedJobId = ref<string | null>(null);
const { data: selectedJobLogs } = useJobLogs(
  computed(() => activeQueue.value),
  computed(() => selectedJobId.value || ''),
);

const viewJobLogs = (jobId: string) => {
  selectedJobId.value = jobId;
  showLogsModal.value = true;
};

const formatDate = (timestamp: number) => {
  return DateTime.fromMillis(timestamp).toLocaleString(DateTime.DATETIME_SHORT);
};

const { data: availableQueues } = useAvailableQueues();

const expandedJobs = ref(new Set<string>());

const toggleJobExpand = (jobId: string) => {
  if (expandedJobs.value.has(jobId)) {
    expandedJobs.value.delete(jobId);
  } else {
    expandedJobs.value.add(jobId);
  }
};

const truncateJson = (data: any) => {
  const jsonStr = JSON.stringify(data, null, 2);
  const lines = jsonStr.split('\n');
  if (lines.length <= 3) return jsonStr;
  return lines.slice(0, 3).join('\n') + '\n...';
};

const { mutate: retryJob, isPending: isRetrying } = useRetryJob();
const { mutate: deleteJob, isPending: isDeleting } = useDeleteJob();

const handleRetry = (jobId: string) => {
  if (!activeQueue.value) return;
  retryJob({ queueName: activeQueue.value, jobId });
};

const handleDelete = (jobId: string) => {
  if (!activeQueue.value) return;
  Alert.fire({
    titleText: t('confirmDeleteJob'),
    icon: 'warning',
    showDenyButton: true,
    denyButtonText: t('delete'),
    showCancelButton: true,
    showConfirmButton: false,
    cancelButtonText: t('cancel'),
  }).then((result) => {
    if (result.isDenied) {
      deleteJob({ queueName: activeQueue.value, jobId });
    }
  });
};

function getJobProgressValue(job: JobDto) {
  if (typeof job.progress === 'number') return job.progress;
  if (job.progress !== null && typeof job.progress === 'object' && 'value' in job.progress)
    return (job.progress as { value: number }).value;
  return 0;
}
</script>

<i18n lang="json">
{
  "en": {
    "queueInspector": "Queue Inspector",
    "active": "Active",
    "waiting": "Waiting",
    "delayed": "Delayed",
    "failed": "Failed",
    "completed": "Completed",
    "waitingChildren": "Waiting for Child Tasks",
    "id": "ID",
    "name": "Name",
    "progress": "Progress",
    "attempts": "Attempts",
    "timestamp": "Timestamp",
    "data": "Data",
    "actions": "Actions",
    "viewLogs": "View Logs",
    "loadMore": "Load More",
    "showMore": "Show More",
    "showLess": "Show Less",
    "retry": "Retry",
    "delete": "Delete",
    "confirmDeleteJob": "Are you sure you want to delete this job?"
  },
  "de": {
    "queueInspector": "Schlangenüberprüfer 🐍🕵️‍♀️",
    "active": "Aktiv",
    "waiting": "Wartend",
    "delayed": "Verzögert",
    "failed": "Fehlgeschlagen",
    "completed": "Abgeschlossen",
    "waitingChildren": "Wartend auf Child Tasks",
    "id": "ID",
    "name": "Name",
    "progress": "Fortschritt",
    "attempts": "Versuche",
    "timestamp": "Zeitstempel",
    "data": "Daten",
    "actions": "Aktionen",
    "viewLogs": "Logs anzeigen",
    "loadMore": "Mehr laden",
    "showMore": "Mehr anzeigen",
    "showLess": "Weniger anzeigen",
    "retry": "Wiederholen",
    "delete": "Löschen",
    "confirmDeleteJob": "Sind Sie sicher, dass Sie diesen Job löschen möchten?"
  },
  "es": {
    "queueInspector": "Inspector de colas",
    "active": "Activos",
    "waiting": "En espera",
    "delayed": "Retrasados",
    "failed": "Fallidos",
    "completed": "Completados",
    "waitingChildren": "Esperando tareas hijas",
    "id": "ID",
    "name": "Nombre",
    "progress": "Progreso",
    "attempts": "Intentos",
    "timestamp": "Marca de tiempo",
    "data": "Datos",
    "actions": "Acciones",
    "viewLogs": "Ver registros",
    "loadMore": "Cargar más",
    "showMore": "Ver más",
    "showLess": "Ver menos",
    "retry": "Reintentar",
    "delete": "Eliminar",
    "confirmDeleteJob": "¿Seguro que desea eliminar este trabajo?"
  }
}
</i18n>
